import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api, { fmtErr, money, fdt, fdate } from "../../api";
import MapView from "../../components/MapView";
import InfoTip from "../../components/InfoTip";
import { StatusPill, Stat } from "../../components/bits";
import { COUNTRIES } from "../../constants";
import DateField from "../../components/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-black text-white text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-800 transition-colors";
const btnOutline = "border border-zinc-300 text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-50 transition-colors";

export function OverviewTab() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState({ jobs: [], drivers: [] });
  const [, setParams] = useSearchParams();
  const load = useCallback(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/live-map").then((r) => setLive(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!data) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;
  const ex = data.exceptions || {};

  const approve = async (id) => {
    try { await api.post(`/admin/users/${id}/status`, { status: "approved" }); toast.success("Approved"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const Queue = ({ title, count, items, render, testId, empty = "Nothing needs attention." }) => (
    <section className="bg-white border border-black/5 rounded-xl p-4" data-testid={testId}>
      <h3 className="text-xs font-bold mb-2.5 flex items-center justify-between">{title}
        <span className="text-[10px] font-semibold text-zinc-400">{count}</span></h3>
      {(!items || items.length === 0) && <p className="text-[11px] text-zinc-400">{empty}</p>}
      <div className="space-y-1.5">{(items || []).map(render)}</div>
    </section>
  );

  return (
    <div className="space-y-5" data-testid="ct-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total jobs" value={data.jobs_total} />
        <Stat label="Open" value={data.jobs.open || 0} />
        <Stat label="In transit" value={data.jobs.in_transit || 0} />
        <Stat label="GMV" value={money(data.gmv)} sub="delivered" />
        <Stat label="Couriers" value={data.users.couriers} sub={`${data.users.pending} pending`} />
        <Stat label="Companies" value={data.users.companies} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Queue title="Approvals" count={data.users.pending} items={ex.pending_users} testId="queue-approvals"
          render={(u) => (
            <div key={u.id} className="flex items-center justify-between gap-2 text-xs border border-zinc-100 rounded-lg px-3 py-2">
              <div className="min-w-0"><p className="font-semibold truncate">{u.name}</p>
                <p className="text-zinc-400 truncate">{u.email} · {u.role}</p></div>
              <div className="flex gap-1.5 shrink-0">
                <button data-testid={`q-approve-${u.id}`} onClick={() => approve(u.id)} className={btnBlack}>Approve</button>
                <button data-testid={`q-reject-${u.id}`} onClick={async () => { await api.post(`/admin/users/${u.id}/status`, { status: "rejected" }); load(); }} className={btnOutline}>Reject</button>
              </div>
            </div>
          )} />

        <Queue title="Disputes" count={data.open_disputes} items={ex.open_disputes} testId="queue-disputes"
          render={(d) => (
            <button key={d.id} onClick={() => setParams({ tab: "disputes" })}
              className="w-full text-left text-xs border border-zinc-100 rounded-lg px-3 py-2 hover:border-black/20">
              <span className="font-mono">{d.job_ref}</span> · <span className="text-zinc-500">{d.reason?.slice(0, 60)}</span>
            </button>
          )} />

        <Queue title="Documents expiring" count={data.docs_expiring} items={ex.expiring_docs} testId="queue-docs"
          render={(doc) => (
            <button key={doc.id} onClick={() => setParams({ tab: "documents" })}
              className="w-full text-left text-xs border border-zinc-100 rounded-lg px-3 py-2 hover:border-black/20">
              {doc.name} · <span className="text-zinc-500">expires {fdate(doc.expiry_date)}</span>
            </button>
          )} />

        <Queue title="Unassigned / stale jobs" count={(ex.stale_open_jobs || []).length + (ex.unassigned_today || []).length}
          items={Array.from(new Map([...(ex.unassigned_today || []), ...(ex.stale_open_jobs || [])].map((j) => [j.id, j])).values()).slice(0, 8)} testId="queue-jobs"
          render={(j) => (
            <a key={j.id} href={`/jobs/${j.id}`}
              className="block text-xs border border-zinc-100 rounded-lg px-3 py-2 hover:border-black/20">
              <span className="font-mono">{j.ref}</span> · {j.pickup?.city} → {j.delivery?.city}
              <span className="text-zinc-400"> · {fdate(j.pickup_date || j.created_at)}</span>
            </a>
          )} />

        <Queue title="Failed payments" count={(ex.past_due_subscriptions || []).length} items={ex.past_due_subscriptions} testId="queue-payments"
          render={(s) => (
            <button key={s.id || s.user_id} onClick={() => setParams({ tab: "billing" })}
              className="w-full text-left text-xs border border-zinc-100 rounded-lg px-3 py-2 hover:border-black/20">
              {s.plan_name} · past due
            </button>
          )} />

        <Queue title="Integration errors" count={(ex.sync_errors || []).length} items={ex.sync_errors} testId="queue-sync-errors"
          render={(l) => (
            <button key={l.id} onClick={() => setParams({ tab: "imports" })}
              className="w-full text-left text-xs border border-zinc-100 rounded-lg px-3 py-2 hover:border-black/20">
              <span className="text-zinc-400 font-mono">{fdt(l.at)}</span> · {l.error?.slice(0, 70)}
            </button>
          )} />
      </div>

      <div className="rounded-xl overflow-hidden border border-black/5" data-testid="ct-live-map">
        <MapView height="400px" jobs={live.jobs} drivers={live.drivers}
          onSelectJob={(id) => { window.location.href = `/jobs/${id}`; }} />
      </div>
      <p className="text-[11px] text-zinc-400">Live map refreshes every 15s. Black dots are couriers reporting real GPS positions.</p>
    </div>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ role: "", status: "", q: "" });
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/users", { params }).then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, [filters]);
  useEffect(load, [load]);

  const act = async (id, action, body) => {
    try { await api.post(`/admin/users/${id}/${action}`, body); toast.success("Updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="ct-users">
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="users-search" placeholder="Search name/email" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className={inputCls} />
        <select data-testid="users-role-filter" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className={inputCls}>
          <option value="">All roles</option><option value="courier">Couriers</option><option value="company">Companies</option><option value="admin">Admins</option>
        </select>
        <select data-testid="users-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls}>
          <option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="suspended">Suspended</option>
        </select>
      </div>
      <div className="bg-white border border-black/5 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[760px]">
          <thead><tr className="text-left text-zinc-400 border-b border-zinc-100">
            <th className="p-3 font-semibold">User</th><th className="p-3 font-semibold">Role</th><th className="p-3 font-semibold">Country</th>
            <th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold">Plan</th><th className="p-3 font-semibold">Joined</th><th className="p-3 font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                <td className="p-3"><p className="font-semibold text-zinc-900">{u.name}{u.verified && <span className="text-[#0A0A0A] ml-1">✓</span>}</p>
                  <p className="text-zinc-400">{u.email}</p>{u.company_name && <p className="text-zinc-500">{u.company_name}</p>}</td>
                <td className="p-3 capitalize">{u.role}</td>
                <td className="p-3">{u.country || "—"}</td>
                <td className="p-3"><StatusPill status={u.status} /></td>
                <td className="p-3">
                  <select data-testid={`plan-select-${u.id}`} value={u.subscription_plan_id || ""} onChange={async (e) => { if (!e.target.value) return; try { await api.post(`/admin/billing/users/${u.id}/plan`, { plan_id: e.target.value }); toast.success("Plan assigned"); load(); } catch (err) { toast.error(fmtErr(err)); } }}
                    className={inputCls}>
                    <option value="">{u.subscription_plan || "free"}</option>
                    {plans.filter((p) => p.role === u.role).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td className="p-3 text-zinc-400">{fdate(u.created_at)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    {u.status === "pending" && <>
                      <button data-testid={`approve-${u.id}`} onClick={() => act(u.id, "status", { status: "approved" })} className={btnBlack}>Approve</button>
                      <button data-testid={`reject-${u.id}`} onClick={() => act(u.id, "status", { status: "rejected" })} className={btnOutline}>Reject</button>
                    </>}
                    {u.status === "approved" && !u.verified && <button data-testid={`verify-${u.id}`} onClick={() => act(u.id, "verify")} className={btnOutline}>Verify</button>}
                    {u.status === "approved" && u.role !== "admin" && <button data-testid={`suspend-${u.id}`} onClick={() => act(u.id, "status", { status: "suspended" })} className="border border-red-200 text-red-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-red-50">Suspend</button>}
                    {u.status === "suspended" && <button data-testid={`reactivate-${u.id}`} onClick={() => act(u.id, "status", { status: "approved" })} className={btnBlack}>Reactivate</button>}
                    <button data-testid={`edit-user-${u.id}`} onClick={() => { setEditing(u); setForm({ name: u.name || "", phone: u.phone || "", country: u.country || "", company_name: u.company_name || "", vehicle_type: u.vehicle_type || "" }); }} className={btnOutline}>Edit</button>
                    {u.role !== "admin" && (
                      <button data-testid={`delete-user-${u.id}`} onClick={async () => {
                        if (!window.confirm(`Delete ${u.email} and all their jobs, bids and data? This cannot be undone.`)) return;
                        try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
                        catch (err) { toast.error(fmtErr(err)); }
                      }} className="border border-red-200 text-red-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-red-50">Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-xs text-zinc-400 text-center py-10">No users match.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.email}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <input data-testid="edit-user-name" placeholder="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="edit-user-phone" placeholder="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inputCls} w-full`} />
            {editing?.role === "company" && (
              <input data-testid="edit-user-company" placeholder="Company name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={`${inputCls} w-full`} />
            )}
            <select data-testid="edit-user-country" value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className={`${inputCls} w-full`}>
              <option value="">Country…</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <button data-testid="edit-user-save" onClick={async () => {
              try { await api.patch(`/admin/users/${editing.id}`, form); toast.success("Saved"); setEditing(null); load(); }
              catch (err) { toast.error(fmtErr(err)); }
            }} className={`${btnBlack} w-full !py-2.5`}>Save changes</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [jform, setJform] = useState({});
  const load = useCallback(() => {
    api.get("/admin/jobs", { params: { status: status || undefined, q: q || undefined } })
      .then((r) => setJobs(r.data.jobs)).catch(() => {});
  }, [status, q]);
  useEffect(load, [load]);
  const force = async (id, s) => {
    try { await api.post(`/jobs/${id}/status`, { status: s }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const delJob = async (j) => {
    if (!window.confirm(`Delete job ${j.ref} and its bids? This cannot be undone.`)) return;
    try { await api.delete(`/admin/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div data-testid="ct-jobs">
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="jobs-search" placeholder="Search ref" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls} />
        <select data-testid="jobs-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="">All statuses</option>
          {["open", "assigned", "picked_up", "in_transit", "delivered", "disputed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <div className="bg-white border border-black/5 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[760px]">
          <thead><tr className="text-left text-zinc-400 border-b border-zinc-100">
            <th className="p-3 font-semibold">Ref</th><th className="p-3 font-semibold">Route</th><th className="p-3 font-semibold">Company</th>
            <th className="p-3 font-semibold">Source</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold">Price</th><th className="p-3 font-semibold">Force status</th><th className="p-3 font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.ref}`} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                <td className="p-3 font-mono"><a href={`/jobs/${j.id}`} className="text-[#0A0A0A] hover:underline">{j.ref}</a></td>
                <td className="p-3">{j.pickup?.city || "—"} → {j.delivery?.city || "—"}</td>
                <td className="p-3">{j.company_name}</td>
                <td className="p-3 capitalize">{j.source}</td>
                <td className="p-3"><StatusPill status={j.status} /></td>
                <td className="p-3 font-semibold">{money(j.final_price ?? j.price_net, j.currency)}</td>
                <td className="p-3">
                  <select data-testid={`force-status-${j.ref}`} value="" onChange={(e) => e.target.value && force(j.id, e.target.value)} className={inputCls}>
                    <option value="">Change…</option>
                    {["open", "assigned", "picked_up", "in_transit", "delivered", "disputed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex gap-1.5">
                    <button data-testid={`edit-job-${j.ref}`} onClick={() => { setEditing(j); setJform({ status: j.status, price_net: j.price_net ?? "", currency: j.currency || "EUR", pickup_date: j.pickup_date || "" }); }} className={btnOutline}>Edit</button>
                    <button data-testid={`delete-job-${j.ref}`} onClick={() => delJob(j)} className="border border-red-200 text-red-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-red-50">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobs.length === 0 && <p className="text-xs text-zinc-400 text-center py-10">No jobs yet. Jobs appear here as companies post them or imports are approved.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="edit-job-dialog">
          <DialogHeader><DialogTitle>Edit job {editing?.ref}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <select data-testid="edit-job-status" value={jform.status || ""} onChange={(e) => setJform({ ...jform, status: e.target.value })} className={`${inputCls} w-full`}>
              {["draft", "open", "assigned", "picked_up", "in_transit", "delivered", "disputed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
            <div className="flex gap-2">
              <input data-testid="edit-job-price" type="number" min="0" step="0.01" placeholder="Net price" value={jform.price_net ?? ""} onChange={(e) => setJform({ ...jform, price_net: e.target.value === "" ? null : Number(e.target.value) })} className={`${inputCls} w-full`} />
              <select data-testid="edit-job-currency" value={jform.currency || "EUR"} onChange={(e) => setJform({ ...jform, currency: e.target.value })} className={inputCls}>
                <option value="EUR">EUR</option><option value="GBP">GBP</option>
              </select>
            </div>
            <DateField value={jform.pickup_date || ""} onChange={(val) => setJform({ ...jform, pickup_date: val })} testId="edit-job-date" placeholder="Pickup date" className="w-full" />
            <button data-testid="edit-job-save" onClick={async () => {
              try { await api.patch(`/admin/jobs/${editing.id}`, jform); toast.success("Saved"); setEditing(null); load(); }
              catch (err) { toast.error(fmtErr(err)); }
            }} className={`${btnBlack} w-full !py-2.5`}>Save changes</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DisputesTab() {
  const [disputes, setDisputes] = useState([]);
  const [resolveFor, setResolveFor] = useState(null);
  const [resolution, setResolution] = useState("");
  const load = () => api.get("/admin/disputes").then((r) => setDisputes(r.data.disputes)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="ct-disputes">
      {disputes.length === 0 && <p className="text-xs text-[#A09D95] py-10 text-center">No disputes. Resolved and open disputes appear here.</p>}
      <div className="space-y-3">
        {disputes.map((d) => (
          <div key={d.id} data-testid={`dispute-${d.id}`} className="bg-white border border-black/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs">{d.job_ref}</span>
              <StatusPill status={d.status} />
            </div>
            <p className="text-sm text-zinc-800">{d.reason}</p>
            <p className="text-[11px] text-zinc-400 mt-1">By {d.opened_by_name} · {fdt(d.created_at)}</p>
            {d.resolution && <p className="text-xs text-green-700 bg-green-50 rounded-lg p-2 mt-2">Resolution: {d.resolution}</p>}
            {d.status === "open" && (
              <button data-testid={`resolve-${d.id}`} onClick={() => setResolveFor(d)} className={`${btnBlack} mt-3`}>Resolve</button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={!!resolveFor} onOpenChange={() => setResolveFor(null)}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Resolve dispute {resolveFor?.job_ref}</DialogTitle></DialogHeader>
          <textarea data-testid="resolution-text" value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3}
            placeholder="Resolution notes (visible to the reporter)" className="w-full border border-zinc-300 rounded-lg text-sm px-3.5 py-2.5 outline-none focus:border-[#0A0A0A] mt-2" />
          <button data-testid="resolve-submit" onClick={async () => {
            try { await api.post(`/admin/disputes/${resolveFor.id}/resolve`, { resolution }); toast.success("Resolved"); setResolveFor(null); setResolution(""); load(); }
            catch (e) { toast.error(fmtErr(e)); }
          }} disabled={resolution.length < 3} className="w-full mt-3 bg-black text-white text-sm font-medium px-5 py-2.5 rounded-lg disabled:opacity-40">Resolve</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/documents", { params: { expiring: expiringOnly } }).then((r) => setDocs(r.data.documents)).catch(() => {});
  }, [expiringOnly]);
  useEffect(load, [load]);
  const verify = async (id, verified) => {
    try { await api.post(`/admin/documents/${id}/verify`, { verified }); toast.success(verified ? "Verified" : "Rejected"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div data-testid="ct-documents">
      <label className="flex items-center gap-2 text-xs mb-4">
        <input type="checkbox" data-testid="docs-expiring-filter" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} className="accent-[#1C1B1A]" />
        Expiring within 30 days only
      </label>
      {docs.length === 0 && <p className="text-xs text-[#A09D95] py-10 text-center">No documents to review.</p>}
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} data-testid={`admin-doc-${d.id}`} className="bg-white border border-black/5 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{d.name} <span className="text-zinc-400 font-normal">· {d.user?.name} ({d.user?.role})</span></p>
              <p className="text-[11px] text-zinc-500 capitalize">{d.doc_type.replace(/_/g, " ")}{d.expiry_date ? ` · expires ${fdate(d.expiry_date)}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              {d.file_url && <a data-testid={`doc-view-${d.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className={btnOutline}>View</a>}
              <StatusPill status={d.verified ? "approved" : "pending"} />
              {!d.verified && <>
                <button data-testid={`doc-verify-${d.id}`} onClick={() => verify(d.id, true)} className={btnBlack}>Verify</button>
                <button data-testid={`doc-reject-${d.id}`} onClick={() => verify(d.id, false)} className={btnOutline}>Reject</button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeesTab() {
  const [fees, setFees] = useState([]);
  const load = () => api.get("/admin/fees").then((r) => setFees(r.data.fees)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="ct-fees">
      {fees.length === 0 && <p className="text-xs text-[#A09D95] py-10 text-center">Platform fees are recorded automatically when jobs are delivered.</p>}
      <div className="bg-white border border-black/5 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[560px]">
          <thead><tr className="text-left text-zinc-400 border-b border-zinc-100">
            <th className="p-3 font-semibold">Job</th><th className="p-3 font-semibold">Amount</th><th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold"></th>
          </tr></thead>
          <tbody>
            {fees.map((f) => (
              <tr key={f.id} data-testid={`fee-${f.id}`} className="border-b border-zinc-50">
                <td className="p-3 font-mono">{f.job_ref}</td>
                <td className="p-3 font-semibold">{money(f.amount, f.currency)}</td>
                <td className="p-3 text-zinc-400">{fdate(f.created_at)}</td>
                <td className="p-3"><StatusPill status={f.status} /></td>
                <td className="p-3">{f.status === "pending" && (
                  <button data-testid={`fee-paid-${f.id}`} onClick={async () => { await api.post(`/admin/fees/${f.id}/status`, { status: "paid" }); load(); }} className={btnBlack}>Mark paid</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReportsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;
  const jobsData = data.jobs_by_day.map((d) => ({ day: d._id?.slice(5), jobs: d.n }));
  const revData = data.revenue_by_day.map((d) => ({ day: d._id?.slice(5), revenue: Math.round(d.t || 0) }));
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ct-reports">
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-4">Jobs per day</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={jobsData}><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip />
            <Area type="monotone" dataKey="jobs" stroke="#0A0A0A" fill="#0A0A0A" fillOpacity={0.12} /></AreaChart>
        </ResponsiveContainer>
      </section>
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-4">Revenue per day (delivered)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={revData}><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip />
            <Area type="monotone" dataKey="revenue" stroke="#18181B" fill="#18181B" fillOpacity={0.08} /></AreaChart>
        </ResponsiveContainer>
      </section>
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Jobs by country</h3>
        {data.by_country.length === 0 && <p className="text-xs text-zinc-400">No data yet.</p>}
        {data.by_country.map((c, i) => (
          <div key={c._id || i} className="flex justify-between text-xs py-1.5 border-b border-zinc-50 last:border-0">
            <span>{c._id || "Unknown"}</span><span className="font-bold">{c.n}</span>
          </div>
        ))}
      </section>
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Top couriers</h3>
        {data.top_couriers.length === 0 && <p className="text-xs text-zinc-400">No completed deliveries yet.</p>}
        {data.top_couriers.map((c, i) => (
          <div key={c._id || i} className="flex justify-between text-xs py-1.5 border-b border-zinc-50 last:border-0">
            <span>{c.name || "—"}</span><span className="font-bold">{c.n} jobs · {money(c.t)}</span>
          </div>
        ))}
      </section>
    </div>
  );
}

export function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/audit", { params: { q: q || undefined } }).then((r) => setEntries(r.data.entries)).catch(() => {}); }, [q]);
  return (
    <div data-testid="ct-audit">
      <input data-testid="audit-search" placeholder="Search actor or action" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} mb-4`} />
      <div className="bg-white border border-black/5 rounded-xl divide-y divide-zinc-50">
        {entries.map((e) => (
          <div key={e.id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 text-xs">
            <span className="font-mono text-zinc-400 w-36 shrink-0">{fdt(e.at)}</span>
            <span className="font-semibold">{e.action}</span>
            <span className="text-zinc-500">{e.entity} · {e.actor_email}</span>
          </div>
        ))}
        {entries.length === 0 && <p className="text-xs text-zinc-400 text-center py-10">No audit entries.</p>}
      </div>
    </div>
  );
}

export function SettingsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data.settings)).catch(() => {}); }, []);
  if (!settings) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;
  const pricing = settings.pricing || {};
  const savePricing = async () => {
    try { await api.put("/admin/settings", { key: "pricing", value: pricing }); toast.success("Pricing saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const saveSeo = async () => {
    try { await api.put("/admin/settings", { key: "seo", value: settings.seo }); toast.success("SEO saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const num = (v) => (v === "" ? "" : parseFloat(v));
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ct-settings">
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-1 flex items-center">Pricing engine<InfoTip text="Quotes = (base fee + per-km rate × distance) × service multiplier, plus VAT. Platform fee is charged to companies on delivered jobs." /></h3>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <label className="text-xs">Base fee €<input data-testid="set-base-fee" type="number" step="0.5" value={pricing.base_fee ?? ""} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, base_fee: num(e.target.value) } })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="text-xs">VAT rate<input data-testid="set-vat" type="number" step="0.01" value={pricing.vat_rate ?? ""} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, vat_rate: num(e.target.value) } })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="text-xs">Platform fee %<input data-testid="set-fee" type="number" step="0.01" value={pricing.platform_fee_pct ?? ""} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, platform_fee_pct: num(e.target.value) } })} className={`${inputCls} w-full mt-1`} /></label>
        </div>
        <p className="text-xs font-semibold text-[#78756E] uppercase tracking-wide mt-4 mb-2">Currencies</p>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs">EUR → GBP rate<input data-testid="set-fx-gbp" type="number" step="0.01" value={pricing.fx?.GBP ?? ""} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, fx: { ...(pricing.fx || {}), GBP: num(e.target.value) } } })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="text-xs">Annual discount %<input data-testid="set-annual-discount" type="number" step="1" value={pricing.annual_discount_pct ?? ""} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, annual_discount_pct: num(e.target.value) } })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="text-xs">Default currency<input data-testid="set-default-currency" value={pricing.currency || "EUR"} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, currency: e.target.value.toUpperCase() } })} className={`${inputCls} w-full mt-1`} /></label>
        </div>
        <p className="text-xs font-semibold text-[#78756E] uppercase tracking-wide mt-4 mb-2">€/km by vehicle</p>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(pricing.per_km || {}).map(([k, v]) => (
            <label key={k} className="text-[11px] capitalize">{k.replace(/_/g, " ")}
              <input data-testid={`set-perkm-${k}`} type="number" step="0.1" value={v} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, per_km: { ...pricing.per_km, [k]: num(e.target.value) } } })} className={`${inputCls} w-full mt-1`} />
            </label>
          ))}
        </div>
        <p className="text-xs font-semibold text-[#78756E] uppercase tracking-wide mt-4 mb-2">Service multipliers</p>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(pricing.service_multiplier || {}).map(([k, v]) => (
            <label key={k} className="text-[11px] capitalize">{k.replace(/_/g, " ")}
              <input data-testid={`set-mult-${k}`} type="number" step="0.1" value={v} onChange={(e) => setSettings({ ...settings, pricing: { ...pricing, service_multiplier: { ...pricing.service_multiplier, [k]: num(e.target.value) } } })} className={`${inputCls} w-full mt-1`} />
            </label>
          ))}
        </div>
        <button data-testid="save-pricing-btn" onClick={savePricing} className={`${btnBlack} mt-4`}>Save pricing</button>
      </section>
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">SEO defaults</h3>
        <label className="text-xs block mb-3">Site title
          <input data-testid="seo-title" value={settings.seo?.site_title || ""} onChange={(e) => setSettings({ ...settings, seo: { ...settings.seo, site_title: e.target.value } })} className={`${inputCls} w-full mt-1`} /></label>
        <label className="text-xs block mb-3">Meta description
          <textarea data-testid="seo-description" rows={3} value={settings.seo?.site_description || ""} onChange={(e) => setSettings({ ...settings, seo: { ...settings.seo, site_description: e.target.value } })} className={`${inputCls} w-full mt-1`} /></label>
        <button data-testid="save-seo-btn" onClick={saveSeo} className={btnBlack}>Save SEO</button>
      </section>
      <AdminAccessSection />
      <FeedKeysSection settings={settings} setSettings={setSettings} />
    </div>
  );
}

function FeedKeysSection({ settings, setSettings }) {
  const keys = settings.resource_keys || {};
  const fields = [
    ["traffic_scotland", "Traffic Scotland API key"],
    ["tfl", "TfL Unified API key"],
    ["met_office", "Met Office Weather DataHub key"],
  ];
  const save = async () => {
    try { await api.put("/admin/settings", { key: "resource_keys", value: keys }); toast.success("Feed keys saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <section className="cr-card !p-5 lg:col-span-2" data-testid="ct-feed-keys">
      <h3 className="text-sm font-bold mb-1 flex items-center">External feed API keys
        <InfoTip text="Optional official feeds for the Transport Resources pages. Sources without a key show 'Pending connection' — no fake data is ever displayed. Keys are stored server-side only." /></h3>
      <p className="text-[11px] text-[#A09D95] mb-3">Leave blank until the provider approves access. More official European traffic feeds can be added the same way.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {fields.map(([k, label]) => (
          <label key={k} className="text-xs block">{label}
            <input data-testid={`feed-key-${k}`} type="password" autoComplete="off" placeholder={keys[k] ? "•••••••• (set)" : "Not connected"}
              value={keys[k] || ""} onChange={(e) => setSettings({ ...settings, resource_keys: { ...keys, [k]: e.target.value.trim() } })}
              className={`${inputCls} w-full mt-1`} />
          </label>
        ))}
      </div>
      <button data-testid="save-feed-keys-btn" onClick={save} className={`${btnBlack} mt-4`}>Save feed keys</button>
    </section>
  );
}

function AdminAccessSection() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/admins").then((r) => { setAdmins(r.data.admins); setOwnerEmail(r.data.owner_email); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/admins", form);
      toast.success(`Invite sent to ${form.email} — they'll set their own password from the email`);
      setForm({ email: "", name: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <section className="cr-card !p-5 lg:col-span-2" data-testid="ct-admin-access">
      <h3 className="text-sm font-bold mb-1 flex items-center">Control Tower access
        <InfoTip text="Invite trusted people as Control Tower admins. They receive an email to set their own password, then sign in at /control-tower. The primary owner account can't be revoked." /></h3>
      <div className="grid lg:grid-cols-2 gap-4 mt-3">
        <div>
          {(admins || []).map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b border-[#F6F3EA] last:border-0 text-xs" data-testid={`admin-row-${a.id}`}>
              <div className="min-w-0">
                <p className="font-semibold truncate">{a.name} {a.email === ownerEmail && <span className="cr-pill-yellow ml-1">owner</span>}</p>
                <p className="text-[11px] text-[#A09D95] truncate">{a.email}</p>
              </div>
              {a.id !== user?.id && a.email !== ownerEmail && (
                <button data-testid={`admin-revoke-${a.id}`} onClick={async () => {
                  if (!window.confirm(`Revoke Control Tower access for ${a.email}?`)) return;
                  try { await api.delete(`/admin/admins/${a.id}`); toast.success("Access revoked"); load(); }
                  catch (err) { toast.error(fmtErr(err)); }
                }} className="text-[#A09D95] hover:text-red-600 transition-colors shrink-0 ml-3">Revoke</button>
              )}
            </div>
          ))}
          {admins === null && <p className="text-xs text-[#A09D95]">Loading…</p>}
        </div>
        <form onSubmit={invite} className="space-y-2">
          <input data-testid="admin-invite-name" placeholder="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} />
          <input data-testid="admin-invite-email" type="email" required placeholder="Email address" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-full`} />
          <button data-testid="admin-invite-btn" disabled={busy} className={`${btnBlack} disabled:opacity-40`}>
            {busy ? "Sending…" : "Invite admin"}
          </button>
        </form>
      </div>
    </section>
  );
}
